/**
 * SocialPage — Feed · Ranks · Clans · Messages · Profile
 * All monetary data is % / score only — never raw dollar amounts.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getLevelInfo, STORE_CATALOG } from '../utils';
import {
  getSocialProfile, updateSocialProfile,
  getLeaderboard, getFriendsLeaderboard,
  getFriends, sendFriendRequest, acceptFriendRequest, removeFriend,
  getMyClan, getPublicClans, createClan, joinClan, getClanDetails, leaveClan, deleteClan,
  getFeed, getClanFeed, createPost, deletePost, getComments, addComment,
  getBattles, createBattle, acceptBattle, declineBattle,
  getClanBattles, createClanBattle, acceptClanBattle,
  reactToPost,
  votePost,
  editPost,
  reportPost,
  getNotifications, markNotificationsRead,
  getFriendActivities,
  getConversations, getDMs, sendDM,
  getChallenge, joinChallenge,
  getAccountability, requestAccountabilityBuddy, acceptAccountabilityBuddy, removeAccountabilityBuddy,
  getSeasons,
  getBoss, hitBoss,
  getPublicGoals, getMyPublicGoals, createPublicGoal, updatePublicGoal, deletePublicGoal,
} from '../api';

// ── Constants ─────────────────────────────────────────────────────────────────

const INNER_TABS = [
  { id: 'feed',     label: '🌐 Feed' },
  { id: 'compete',  label: '🏆 Compete' },
  { id: 'messages', label: '💬 DMs' },
];

const COMPETE_TABS = [
  { id: 'ranks', label: '🏆 Ranks' },
  { id: 'clans', label: '⚔️ Clans' },
  { id: 'boss',  label: '🐉 Boss' },
  { id: 'goals', label: '🎯 Goals' },
];

const POST_TYPES = [
  { id: 'tip',         label: '💡 Tip',          col: 'var(--ylw)' },
  { id: 'brag',        label: '🏅 Brag',         col: 'var(--grn)' },
  { id: 'achievement', label: '🎯 Achievement',  col: 'var(--blu)' },
  { id: 'question',    label: '❓ Question',     col: 'var(--pnk)' },
  { id: 'confession',  label: '🤫 Confession',   col: '#a29bfe' },
];

const BATTLE_METRICS = [
  { id: 'savings_rate',    label: 'Best Savings Rate',  em: '💵' },
  { id: 'budget_adherence', label: 'Budget Adherence',  em: '📊' },
  { id: 'debt_payoff',     label: 'Most Debt Paid',     em: '📉' },
  { id: 'streak',          label: 'Longest Streak',     em: '🔥' },
];

const REACTION_EMOJIS = ['❤️', '🔥', '💪', '👏'];

const AVATAR_EMOJIS = ['😊','🦁','🐯','🦊','🐼','🐸','🦄','🐧','🦋','🌟','💎','🚀','🎯','💰','🏆','🔥','⚡','🌈','🍀','🎮'];
const AVATAR_COLORS = ['#4d96ff','#ff6bdf','#6bcb77','#ffd93d','#ff6b6b','#7C6FF7','#00cec9','#e17055','#fd79a8','#a29bfe'];

const ACTIVITY_LABELS = {
  level_up:     (d) => `reached Level ${d.level}! 🎉`,
  savings_goal: (d) => `hit their ${d.goal || 'savings'} goal! 💰`,
  debt_paid:    (d) => `paid off ${d.name || 'a debt'}! 🎊`,
  streak:       (d) => `is on a ${d.streak || '?'}-month budget streak! 🔥`,
  budget_clean: ()  => `had a perfect budget month! ✨`,
  clan_joined:  (d) => `joined ${d.clan || 'a clan'}! ⚔️`,
};

const BADGES = [
  { level: 1,  em: '🌱', name: 'Getting Started' },
  { level: 5,  em: '📊', name: 'Leaderboard Access' },
  { level: 10, em: '🌙', name: 'Midnight Theme' },
  { level: 15, em: '⚔️', name: 'Clan Founder' },
  { level: 20, em: '🏆', name: 'Legend Badge' },
];

// ── Shared Components ─────────────────────────────────────────────────────────

function Avatar({ emoji = '😊', color = '#4d96ff', size = 34 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.52), flexShrink: 0,
    }}>
      {emoji}
    </div>
  );
}

function LvBadge({ level = 1, size = 11 }) {
  return (
    <span style={{ fontSize: size, fontWeight: 900, color: 'var(--ylw)', background: 'rgba(255,217,61,.12)', borderRadius: 6, padding: '1px 5px' }}>
      Lv{level}
    </span>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${active === t.id ? 'var(--ylw)' : 'var(--bdr)'}`,
            background: active === t.id ? 'rgba(255,217,61,.12)' : 'none',
            color: active === t.id ? 'var(--ylw)' : 'var(--mut)',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
}

// ── PostCard ──────────────────────────────────────────────────────────────────

function PostCard({ post, onDelete }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [showReactions, setShowReactions] = useState(false);
  const [myVote, setMyVote] = useState(post.my_vote || null);
  const [voteScore, setVoteScore] = useState(post.votes_score || 0);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [content, setContent] = useState(post.content);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const pt = POST_TYPES.find((t) => t.id === post.post_type) || POST_TYPES[0];
  const isAnon = post.is_anonymous;
  const name = isAnon ? 'Anonymous 🤫' : (post.display_name || post.username);

  const handleReact = async (reaction) => {
    const r = await reactToPost(post.id, reaction);
    const prev = myReaction;
    const next = r.reaction;
    setMyReaction(next);
    // update like count: removing → -1, adding → +1, changing → 0
    if (!prev && next) setLikes(l => l + 1);
    else if (prev && !next) setLikes(l => l - 1);
    setShowReactions(false);
    // onReact hook removed — state is local
  };

  const loadComments = async () => {
    if (!showComments) {
      const r = await getComments(post.id);
      if (r.comments) setComments(r.comments);
    }
    setShowComments(s => !s);
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    const r = await addComment(post.id, commentText.trim());
    if (r.comment) { setComments(c => [...c, r.comment]); setCommentText(''); }
  };

  return (
    <div style={{ background: 'var(--surf2)', borderRadius: 12, border: '1px solid var(--bdr)', padding: '14px 16px', marginBottom: 10 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Avatar emoji={isAnon ? '🤫' : (post.avatar_emoji || '😊')} color={isAnon ? '#a29bfe' : (post.avatar_color || '#4d96ff')} size={34} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{name}</span>
            <LvBadge level={post.level} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--mut)' }}>{timeAgo(post.created_at)}</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: pt.col, padding: '2px 8px', borderRadius: 20, border: `1px solid ${pt.col}22`, background: `${pt.col}11` }}>
          {pt.label}
        </span>
        {post.is_own && !isAnon && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setEditing(e => !e)} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}>✏️</button>
            <button onClick={() => onDelete(post.id)} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        )}
      </div>

      {/* Content / Edit */}
      {editing ? (
        <div style={{ marginBottom: 10 }}>
          <textarea
            className="ob-inp"
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            maxLength={500}
            rows={3}
            style={{ width: '100%', resize: 'none', marginBottom: 6, fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={async () => { const r = await editPost(post.id, editContent); if (r.ok) { setContent(editContent); setEditing(false); } }} style={{ padding: '6px 14px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 12 }}>Save</button>
            <button onClick={() => { setEditing(false); setEditContent(content); }} style={{ padding: '6px 10px', borderRadius: 8, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--txt)', marginBottom: 10 }}>{content}</div>
      )}

      {/* Attached stat */}
      {post.attached_stat && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(107,203,119,.1)', border: '1px solid rgba(107,203,119,.3)', borderRadius: 20, fontSize: 12, color: 'var(--grn)', fontWeight: 700, marginBottom: 10 }}>
          ✓ {post.attached_stat.label}
          {post.attached_stat.value && <span style={{ opacity: 0.8 }}>— {post.attached_stat.value}</span>}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowReactions(s => !s)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: myReaction ? 'var(--red)' : 'var(--mut)', fontWeight: myReaction ? 700 : 400 }}
          >
            {myReaction || '🤍'} {likes}
          </button>
          {showReactions && (
            <div style={{ position: 'absolute', top: -44, left: 0, display: 'flex', gap: 4, background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 24, padding: '6px 10px', zIndex: 10, boxShadow: '0 4px 16px #0006' }}>
              {REACTION_EMOJIS.map(em => (
                <button
                  key={em}
                  onClick={() => handleReact(em)}
                  style={{ background: myReaction === em ? 'rgba(255,255,255,.12)' : 'none', border: 'none', fontSize: 18, cursor: 'pointer', borderRadius: 8, padding: '2px 4px', transform: myReaction === em ? 'scale(1.2)' : 'scale(1)', transition: 'transform .15s' }}
                >
                  {em}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={loadComments}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--mut)' }}
        >
          💬 {post.comments_count || 0}
        </button>
        {/* Community votes */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
          <button
            onClick={async () => {
              const r = await votePost(post.id, 1);
              const prev = myVote;
              setMyVote(r.vote);
              setVoteScore(s => s + (r.vote === 1 ? 1 : prev === 1 ? -1 : -2));
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: myVote === 1 ? 'var(--grn)' : 'var(--mut)', fontWeight: myVote === 1 ? 900 : 400 }}
          >▲</button>
          <span style={{ fontSize: 12, fontWeight: 700, color: voteScore > 0 ? 'var(--grn)' : voteScore < 0 ? 'var(--red)' : 'var(--mut)', minWidth: 20, textAlign: 'center' }}>
            {voteScore}
          </span>
          <button
            onClick={async () => {
              const r = await votePost(post.id, -1);
              const prev = myVote;
              setMyVote(r.vote);
              setVoteScore(s => s + (r.vote === -1 ? -1 : prev === -1 ? 1 : 2));
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: myVote === -1 ? 'var(--red)' : 'var(--mut)', fontWeight: myVote === -1 ? 900 : 400 }}
          >▼</button>
        </div>
      </div>

      {/* Report */}
      {!post.is_own && (
        <div style={{ marginTop: 6 }}>
          {reporting ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px', background: 'var(--surf2)', borderRadius: 8, border: '1px solid var(--bdr)' }}>
              <span style={{ fontSize: 11, color: 'var(--mut)', width: '100%', marginBottom: 4 }}>Report reason:</span>
              {['spam', 'harassment', 'misinformation', 'inappropriate', 'other'].map(r => (
                <button key={r} onClick={async () => { await reportPost(post.id, r); setReported(true); setReporting(false); }} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, border: '1.5px solid var(--red)', background: 'rgba(255,107,107,.1)', color: 'var(--red)', cursor: 'pointer', fontWeight: 700, textTransform: 'capitalize' }}>
                  {r}
                </button>
              ))}
              <button onClick={() => setReporting(false)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>Cancel</button>
            </div>
          ) : reported ? (
            <div style={{ fontSize: 10, color: 'var(--mut)' }}>✓ Reported — we'll review it</div>
          ) : (
            <button onClick={() => setReporting(true)} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 10, opacity: 0.5 }}>
              🚩 Report
            </button>
          )}
        </div>
      )}

      {/* Comments */}
      {showComments && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--bdr)', paddingTop: 10 }}>
          {comments.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: 'var(--txt)' }}>{c.display_name || c.username}</span>
              <LvBadge level={c.level} size={9} />
              <span style={{ color: 'var(--mut)', flex: 1 }}>{c.content}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input
              className="ob-inp"
              placeholder="Add a comment…"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitComment()}
              style={{ flex: 1, margin: 0, fontSize: 12 }}
            />
            <button onClick={submitComment} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 700, fontSize: 11, border: 'none', cursor: 'pointer' }}>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity Feed ─────────────────────────────────────────────────────────────

function ActivityFeedView() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFriendActivities().then(r => {
      if (r.activities) setActivities(r.activities);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="em">Loading…</div>;
  if (!activities.length) return <div className="em">No friend activity yet — add friends and check back! 👥</div>;

  return (
    <div>
      {activities.map((a) => {
        const fn = ACTIVITY_LABELS[a.type];
        const msg = fn ? fn(a.data || {}) : a.type;
        const name = a.display_name || a.username;
        return (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bdr)' }}>
            <Avatar emoji={a.avatar_emoji || '😊'} color={a.avatar_color || '#4d96ff'} size={34} />
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{name}</span>
              {' '}
              <span style={{ fontSize: 12, color: 'var(--mut)' }}>{msg}</span>
              <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>{timeAgo(a.created_at)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Feed View ─────────────────────────────────────────────────────────────────

function FeedView({ profile, levelInfo, clanId, showToast }) {
  const [feedTab, setFeedTab] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('tip');
  const [attachStat, setAttachStat] = useState(false);
  const [statLabel, setStatLabel] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = clanId ? await getClanFeed(clanId) : await getFeed();
    if (r.posts) setPosts(r.posts);
    setLoading(false);
  }, [clanId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!content.trim()) return;
    const attachedStat = attachStat && statLabel ? { label: statLabel, value: levelInfo ? `Lv${levelInfo.level}` : '' } : null;
    const r = await createPost({ content, postType, attachedStat, clanId: clanId || undefined, isAnonymous });
    if (r.post) {
      setPosts(p => [{ ...r.post, avatar_emoji: isAnonymous ? null : (profile?.avatar_emoji || '😊'), avatar_color: isAnonymous ? null : (profile?.avatar_color || '#4d96ff') }, ...p]);
      setContent(''); setComposing(false); setAttachStat(false); setStatLabel(''); setIsAnonymous(false);
      showToast('Posted! 🎉');
    } else showToast(r.error || 'Failed to post');
  };

  const handleDelete = async (id) => {
    await deletePost(id);
    setPosts(p => p.filter(x => x.id !== id));
  };

  const FEED_TABS = [{ id: 'all', label: '🌐 Global' }, { id: 'friends', label: '👥 Friends' }];

  return (
    <div>
      {!clanId && <TabBar tabs={FEED_TABS} active={feedTab} onChange={setFeedTab} />}

      {feedTab === 'friends' ? (
        <ActivityFeedView />
      ) : (
        <>
          {/* Compose */}
          {!composing ? (
            <button
              onClick={() => setComposing(true)}
              style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1px dashed var(--bdr)', background: 'none', color: 'var(--mut)', fontSize: 13, cursor: 'pointer', marginBottom: 14, textAlign: 'left' }}
            >
              💬 Share a tip, brag, or question…
            </button>
          ) : (
            <div style={{ background: 'var(--surf2)', borderRadius: 12, border: '1px solid var(--bdr)', padding: 16, marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {POST_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setPostType(t.id)} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${postType === t.id ? t.col : 'var(--bdr)'}`, background: postType === t.id ? `${t.col}18` : 'none', color: postType === t.id ? t.col : 'var(--mut)' }}>
                    {t.label}
                  </button>
                ))}
              </div>
              <textarea
                className="ob-inp"
                placeholder="What's on your mind? Tips, wins, questions — all welcome."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={500}
                rows={3}
                style={{ width: '100%', resize: 'none', marginBottom: 8, fontSize: 13 }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--mut)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={attachStat} onChange={(e) => setAttachStat(e.target.checked)} />
                  Attach verified stat
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: postType === 'confession' ? '#a29bfe' : 'var(--mut)', cursor: 'pointer', fontWeight: isAnonymous ? 700 : 400 }}>
                  <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} />
                  🤫 Post anonymously
                </label>
                {attachStat && (
                  <input className="ob-inp" placeholder="e.g. Paid off car loan!" value={statLabel} onChange={(e) => setStatLabel(e.target.value)} style={{ flex: 1, margin: 0, fontSize: 12 }} />
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={submit} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}>Post</button>
                <button onClick={() => { setComposing(false); setContent(''); }} style={{ padding: '9px 14px', borderRadius: 10, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="em">Loading feed…</div>
          ) : posts.length === 0 ? (
            <div className="em">No posts yet — be the first! 🎉</div>
          ) : (
            posts.map(p => (
              <PostCard key={p.id} post={p} onDelete={handleDelete} />
            ))
          )}
        </>
      )}
    </div>
  );
}

// ── Monthly Challenge ─────────────────────────────────────────────────────────

function ChallengeView({ username, showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getChallenge().then(r => { setData(r); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="em" style={{ marginTop: 14 }}>Loading challenge…</div>;
  if (!data?.challenge) return <div className="em" style={{ marginTop: 14 }}>No challenge this month.</div>;

  const { challenge, entries, myEntry } = data;

  const handleJoin = async () => {
    setJoining(true);
    const r = await joinChallenge();
    if (r.ok) { showToast('Joined the monthly challenge! 🏁'); load(); }
    else showToast(r.error || 'Failed to join');
    setJoining(false);
  };

  return (
    <div className="card cp" style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 2 }}>🏁 {challenge.title}</div>
        <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 8 }}>{challenge.description}</div>
        {!myEntry ? (
          <button onClick={handleJoin} disabled={joining} style={{ padding: '7px 18px', borderRadius: 20, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 12, border: 'none', cursor: 'pointer' }}>
            {joining ? 'Joining…' : '🏁 Join Challenge (+' + challenge.xp_reward + ' XP)'}
          </button>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--grn)', fontWeight: 700 }}>✓ You're participating — sync happens automatically</div>
        )}
      </div>
      <div className="st" style={{ marginBottom: 8, fontSize: 11 }}>Top Participants</div>
      {entries.length === 0 ? (
        <div className="em">No participants yet — be the first!</div>
      ) : (
        entries.map((e, i) => {
          const isMe = e.username === username;
          return (
            <div key={e.username} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)', background: isMe ? 'rgba(255,217,61,.04)' : 'none', borderRadius: isMe ? 6 : 0 }}>
              <span style={{ width: 24, textAlign: 'center', fontSize: 12, fontWeight: 900, color: i === 0 ? '#FFD700' : 'var(--mut)' }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
              </span>
              <Avatar emoji={e.avatar_emoji || '😊'} color={e.avatar_color || '#4d96ff'} size={28} />
              <div style={{ flex: 1, fontSize: 12, fontWeight: isMe ? 800 : 500, color: isMe ? 'var(--ylw)' : 'var(--txt)' }}>
                {e.display_name || e.username} {isMe && '← you'}
              </div>
              <LvBadge level={e.level} size={10} />
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--grn)', minWidth: 40, textAlign: 'right' }}>
                {Math.round((e.score || 0) * 100)}%
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Seasons View ──────────────────────────────────────────────────────────────

function SeasonsView({ username }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    getSeasons().then(r => setData(r));
  }, []);

  if (!data) return <div className="em" style={{ marginTop: 14 }}>Loading seasons…</div>;

  const { seasons, myMedals } = data;
  const MEDALS = [
    { key: 'gold',   em: '🥇', col: '#FFD700', label: 'Gold' },
    { key: 'silver', em: '🥈', col: '#C0C0C0', label: 'Silver' },
    { key: 'bronze', em: '🥉', col: '#CD7F32', label: 'Bronze' },
  ];

  return (
    <div>
      {/* My medals */}
      {(myMedals.season_gold > 0 || myMedals.season_silver > 0 || myMedals.season_bronze > 0) && (
        <div className="card cp" style={{ marginBottom: 14 }}>
          <div className="st" style={{ marginBottom: 10 }}>🏆 My Season Medals</div>
          <div style={{ display: 'flex', gap: 12 }}>
            {MEDALS.map(m => (
              <div key={m.key} style={{ flex: 1, textAlign: 'center', padding: '10px', background: 'var(--surf2)', borderRadius: 10 }}>
                <div style={{ fontSize: 28 }}>{m.em}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: m.col }}>{myMedals['season_' + m.key] || 0}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="st" style={{ marginBottom: 10 }}>📅 Season History</div>
      {seasons.length === 0 ? (
        <div className="em">No seasons completed yet — first one ends this month!</div>
      ) : (
        seasons.map(s => (
          <div key={s.month} className="card cp" style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>{s.month}</div>
            {[
              { place: '🥇', name: s.gold_display || s.gold_username, emoji: s.gold_emoji, color: s.gold_color },
              { place: '🥈', name: s.silver_display || s.silver_username, emoji: null, color: null },
              { place: '🥉', name: s.bronze_display || s.bronze_username, emoji: null, color: null },
            ].filter(r => r.name).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bdr)' }}>
                <span style={{ fontSize: 18 }}>{r.place}</span>
                {r.emoji && <Avatar emoji={r.emoji} color={r.color || '#4d96ff'} size={26} />}
                <span style={{ fontSize: 13, fontWeight: 600, color: r.name === username ? 'var(--ylw)' : 'var(--txt)' }}>
                  {r.name} {r.name === username && '← you'}
                </span>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

// ── Ranks View ────────────────────────────────────────────────────────────────

function RanksView({ username, showToast }) {
  const [view, setView] = useState('global');
  const [board, setBoard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (view === 'global' ? getLeaderboard() : getFriendsLeaderboard()).then(r => {
      setBoard(r.leaderboard || []);
      if (r.myRank) setMyRank(r.myRank);
      setLoading(false);
    });
  }, [view]);

  return (
    <div>
      <TabBar tabs={[{ id: 'global', label: '🌍 Global' }, { id: 'friends', label: '👥 Friends' }, { id: 'challenge', label: '🏁 Challenge' }, { id: 'seasons', label: '🏆 Seasons' }]} active={view} onChange={setView} />

      {view === 'challenge' ? (
        <ChallengeView username={username} showToast={showToast} />
      ) : view === 'seasons' ? (
        <SeasonsView username={username} />
      ) : (
        <>
          {myRank && view === 'global' && (
            <div style={{ padding: '8px 14px', background: 'rgba(255,217,61,.08)', border: '1px solid rgba(255,217,61,.25)', borderRadius: 10, fontSize: 12, color: 'var(--ylw)', fontWeight: 700, marginBottom: 12 }}>
              You are ranked #{myRank} globally
            </div>
          )}
          <div className="card cp">
            {loading ? (
              <div className="em">Loading…</div>
            ) : board.length === 0 ? (
              <div className="em">No ranked players yet — opt in via Profile!</div>
            ) : (
              board.map((u, i) => {
                const isMe = u.username === username;
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bdr)', background: isMe ? 'rgba(255,217,61,.04)' : 'none', borderRadius: isMe ? 8 : 0 }}>
                    <div style={{ width: 28, textAlign: 'center', fontSize: 13, fontWeight: 900, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'var(--mut)' }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </div>
                    <Avatar emoji={u.avatar_emoji || '😊'} color={u.avatar_color || '#4d96ff'} size={32} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: isMe ? 800 : 600, color: isMe ? 'var(--ylw)' : 'var(--txt)' }}>
                        {u.display_name || u.username} {isMe && '← you'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                        🔥 {u.streak || 0}mo · 💵 saves {Math.round((u.savings_rate || 0) * 100)}% · 🎯 {Math.round((u.goal_progress || 0) * 100)}% to goals
                      </div>
                    </div>
                    <LvBadge level={u.level} />
                    <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--grn)', minWidth: 36, textAlign: 'right' }}>
                      {Math.round(u.rank_score || 0)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Clans View ────────────────────────────────────────────────────────────────

function ClansView({ username, showToast, levelInfo }) {
  const [view, setView] = useState('mine');
  const [myClans, setMyClans] = useState([]);
  const [publicClans, setPublicClans] = useState([]);
  const [activeClan, setActiveClan] = useState(null);
  const [clanDetails, setClanDetails] = useState(null);
  const [clanFeedOpen, setClanFeedOpen] = useState(false);
  const [battles, setBattles] = useState([]);
  const [clanBattles, setClanBattles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', emoji: '💰', isPublic: true });
  const [joinCode, setJoinCode] = useState('');
  const [battleTab, setBattleTab] = useState('1v1');
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [challengeForm, setChallengeForm] = useState({ username: '', metric: 'savings_rate', endDate: '' });
  const [clanBattleForm, setClanBattleForm] = useState({ challengedClanId: '', metric: 'savings_rate', endDate: '' });

  const loadMine = useCallback(async () => {
    const [r, b, cb] = await Promise.all([getMyClan(), getBattles(), getClanBattles()]);
    if (r.clans) setMyClans(r.clans);
    if (b.battles) setBattles(b.battles);
    if (cb.battles) setClanBattles(cb.battles);
  }, []);

  const loadPublic = useCallback(async () => {
    const r = await getPublicClans();
    if (r.clans) setPublicClans(r.clans);
  }, []);

  useEffect(() => { loadMine(); loadPublic(); }, [loadMine, loadPublic]);

  const openClan = async (id) => {
    setActiveClan(id);
    const r = await getClanDetails(id);
    if (r.clan) setClanDetails(r);
    setClanFeedOpen(false);
  };

  const handleCreate = async () => {
    const r = await createClan(form);
    if (r.clan) { showToast('Clan created! 🎉'); setCreating(false); setForm({ name: '', description: '', emoji: '💰', isPublic: true }); loadMine(); }
    else showToast(r.error || 'Failed to create clan');
  };

  const handleJoin = async () => {
    const r = await joinClan(joinCode);
    if (r.clan) { showToast('Joined!'); setJoining(false); setJoinCode(''); loadMine(); }
    else showToast(r.error || 'Invalid code');
  };

  const handleLeave = async (id) => {
    if (!window.confirm('Leave this clan?')) return;
    const r = await leaveClan(id);
    if (r.ok) { setActiveClan(null); setClanDetails(null); loadMine(); showToast('Left clan'); }
    else showToast(r.error || 'Failed');
  };

  const handleDeleteClan = async (id) => {
    if (!window.confirm('Delete this clan? This cannot be undone.')) return;
    const r = await deleteClan(id);
    if (r.ok) { setActiveClan(null); setClanDetails(null); loadMine(); showToast('Clan deleted'); }
    else showToast(r.error || 'Failed');
  };

  const handleChallenge = async () => {
    const r = await createBattle(challengeForm);
    if (r.battle) { showToast('Challenge sent! ⚔️'); setChallengeOpen(false); loadMine(); }
    else showToast(r.error || 'Failed');
  };

  const handleClanBattle = async () => {
    if (!activeClan || !clanBattleForm.challengedClanId || !clanBattleForm.endDate) {
      showToast('Fill in opponent clan ID and end date'); return;
    }
    const r = await createClanBattle({
      challengerClanId: activeClan,
      challengedClanId: parseInt(clanBattleForm.challengedClanId),
      metric: clanBattleForm.metric,
      endDate: clanBattleForm.endDate,
    });
    if (r.battle) { showToast('Clan battle initiated! ⚔️'); setChallengeOpen(false); loadMine(); }
    else showToast(r.error || 'Failed');
  };

  // Clan detail page
  if (activeClan && clanDetails) {
    const { clan, members } = clanDetails;
    const isOwner = clan.owner_name === username;
    const myRole = members.find(m => m.username === username)?.role;
    const canChallenge = myRole === 'owner' || myRole === 'admin';
    const myClanBattles = clanBattles.filter(b => b.challenger_id === clan.id || b.challenged_id === clan.id);

    return (
      <div>
        <button onClick={() => { setActiveClan(null); setClanDetails(null); }} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>← Back</button>

        <div className="card cp" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 36 }}>{clan.emoji}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{clan.name}</div>
              <div style={{ fontSize: 12, color: 'var(--mut)' }}>{members.length} members · {clan.is_public ? 'Public' : 'Private'}</div>
            </div>
          </div>
          {clan.description && <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 10 }}>{clan.description}</div>}
          <div style={{ padding: '8px 12px', background: 'var(--surf2)', borderRadius: 8, fontSize: 11, fontFamily: 'monospace', color: 'var(--ylw)', letterSpacing: 2, marginBottom: 10 }}>
            Invite code: <strong>{clan.invite_code}</strong>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setClanFeedOpen(s => !s)} style={{ flex: 1, padding: '8px', borderRadius: 8, background: 'var(--surf2)', border: '1px solid var(--bdr)', color: 'var(--txt)', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
              {clanFeedOpen ? 'Hide' : '🌐 View'} Clan Wall
            </button>
            {myRole && myRole !== 'owner' && (
              <button onClick={() => handleLeave(clan.id)} style={{ padding: '8px 12px', borderRadius: 8, background: 'none', border: '1px solid var(--red)', color: 'var(--red)', fontSize: 12, cursor: 'pointer' }}>Leave</button>
            )}
            {isOwner && (
              <button onClick={() => handleDeleteClan(clan.id)} style={{ padding: '8px 12px', borderRadius: 8, background: 'none', border: '1px solid var(--red)', color: 'var(--red)', fontSize: 12, cursor: 'pointer' }}>Delete</button>
            )}
          </div>
        </div>

        {clanFeedOpen && <FeedView username={username} levelInfo={levelInfo} clanId={clan.id} showToast={showToast} />}

        {/* Members */}
        <div className="card cp" style={{ marginBottom: 12 }}>
          <div className="st" style={{ marginBottom: 10 }}>👥 Members</div>
          {members.map(m => (
            <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
              <Avatar emoji={m.avatar_emoji || '😊'} color={m.avatar_color || '#4d96ff'} size={32} />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{m.display_name || m.username}</span>
                {m.role === 'owner' && <span style={{ fontSize: 10, color: 'var(--ylw)', marginLeft: 6 }}>👑 owner</span>}
              </div>
              <LvBadge level={m.level} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--grn)' }}>{Math.round(m.rank_score || 0)} pts</span>
            </div>
          ))}
        </div>

        {/* Battles */}
        <div className="card cp">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div className="st" style={{ marginBottom: 0 }}>⚔️ Battles</div>
            {canChallenge && (
              <button onClick={() => setChallengeOpen(s => !s)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20, border: '1.5px solid var(--ylw)', background: 'rgba(255,217,61,.1)', color: 'var(--ylw)', cursor: 'pointer', fontWeight: 700 }}>
                + Challenge
              </button>
            )}
          </div>

          <TabBar tabs={[{ id: '1v1', label: '👤 1v1' }, { id: 'clan', label: '⚔️ Clan vs Clan' }]} active={battleTab} onChange={setBattleTab} />

          {challengeOpen && battleTab === '1v1' && (
            <div style={{ marginBottom: 14, padding: '12px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)' }}>
              <input className="ob-inp" placeholder="Opponent username" value={challengeForm.username} onChange={(e) => setChallengeForm(f => ({ ...f, username: e.target.value }))} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {BATTLE_METRICS.map(m => (
                  <button key={m.id} onClick={() => setChallengeForm(f => ({ ...f, metric: m.id }))} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${challengeForm.metric === m.id ? 'var(--ylw)' : 'var(--bdr)'}`, background: challengeForm.metric === m.id ? 'rgba(255,217,61,.12)' : 'none', color: challengeForm.metric === m.id ? 'var(--ylw)' : 'var(--mut)' }}>
                    {m.em} {m.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="date" value={challengeForm.endDate} onChange={(e) => setChallengeForm(f => ({ ...f, endDate: e.target.value }))} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 12, flex: 1 }} />
                <button onClick={handleChallenge} style={{ padding: '7px 14px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 12, border: 'none', cursor: 'pointer' }}>Send ⚔️</button>
              </div>
            </div>
          )}

          {challengeOpen && battleTab === 'clan' && (
            <div style={{ marginBottom: 14, padding: '12px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)' }}>
              <input className="ob-inp" placeholder="Opponent clan ID (from their invite page)" value={clanBattleForm.challengedClanId} onChange={(e) => setClanBattleForm(f => ({ ...f, challengedClanId: e.target.value }))} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {BATTLE_METRICS.map(m => (
                  <button key={m.id} onClick={() => setClanBattleForm(f => ({ ...f, metric: m.id }))} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${clanBattleForm.metric === m.id ? 'var(--ylw)' : 'var(--bdr)'}`, background: clanBattleForm.metric === m.id ? 'rgba(255,217,61,.12)' : 'none', color: clanBattleForm.metric === m.id ? 'var(--ylw)' : 'var(--mut)' }}>
                    {m.em} {m.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="date" value={clanBattleForm.endDate} onChange={(e) => setClanBattleForm(f => ({ ...f, endDate: e.target.value }))} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 12, flex: 1 }} />
                <button onClick={handleClanBattle} style={{ padding: '7px 14px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 12, border: 'none', cursor: 'pointer' }}>Battle! ⚔️</button>
              </div>
            </div>
          )}

          {battleTab === '1v1' && (
            battles.length === 0 ? (
              <div className="em">No battles yet — challenge someone!</div>
            ) : (
              battles.slice(0, 5).map(b => {
                const isChallenger = b.challenger_name === username;
                const opponent = isChallenger ? b.challenged_name : b.challenger_name;
                const statusCol = b.status === 'active' ? 'var(--grn)' : b.status === 'pending' ? 'var(--ylw)' : b.status === 'declined' ? 'var(--red)' : 'var(--mut)';
                return (
                  <div key={b.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{b.title}</div>
                      <div style={{ fontSize: 10, color: 'var(--mut)' }}>vs <strong>{opponent}</strong> · ends {b.end_date}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: statusCol, padding: '2px 8px', border: `1px solid ${statusCol}`, borderRadius: 20 }}>{b.status}</span>
                    {b.status === 'pending' && !isChallenger && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={async () => { await acceptBattle(b.id); loadMine(); }} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--grn)', color: 'var(--grn)', background: 'none', cursor: 'pointer' }}>Accept</button>
                        <button onClick={async () => { await declineBattle(b.id); loadMine(); }} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--red)', color: 'var(--red)', background: 'none', cursor: 'pointer' }}>Decline</button>
                      </div>
                    )}
                  </div>
                );
              })
            )
          )}

          {battleTab === 'clan' && (
            myClanBattles.length === 0 ? (
              <div className="em">No clan battles yet!</div>
            ) : (
              myClanBattles.map(b => {
                const isChallenger = b.challenger_id === clan.id;
                const opName = isChallenger ? (b.challenged_clan_name || '?') : (b.challenger_clan_name || '?');
                const opEm = isChallenger ? (b.challenged_clan_emoji || '⚔️') : (b.challenger_clan_emoji || '⚔️');
                const statusCol = b.status === 'active' ? 'var(--grn)' : b.status === 'pending' ? 'var(--ylw)' : 'var(--mut)';
                return (
                  <div key={b.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{b.title}</div>
                      <div style={{ fontSize: 10, color: 'var(--mut)' }}>vs {opEm} {opName} · ends {b.end_date}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: statusCol, padding: '2px 8px', border: `1px solid ${statusCol}`, borderRadius: 20 }}>{b.status}</span>
                    {b.status === 'pending' && !isChallenger && canChallenge && (
                      <button onClick={async () => { await acceptClanBattle(b.id); loadMine(); }} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--grn)', color: 'var(--grn)', background: 'none', cursor: 'pointer' }}>Accept</button>
                    )}
                  </div>
                );
              })
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <TabBar tabs={[{ id: 'mine', label: 'My Clans' }, { id: 'browse', label: '🔍 Browse' }]} active={view} onChange={setView} />

      {view === 'mine' && (
        <>
          {myClans.length === 0 ? (
            <div className="em" style={{ marginBottom: 12 }}>You're not in any clans yet.</div>
          ) : (
            myClans.map(c => (
              <div key={c.id} className="card cp" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => openClan(c.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 28 }}>{c.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--mut)' }}>{c.member_count} members · {c.role}</div>
                  </div>
                  <span style={{ fontSize: 14, color: 'var(--mut)' }}>→</span>
                </div>
              </div>
            ))
          )}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button onClick={() => setCreating(s => !s)} style={{ flex: 1, padding: '10px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}>+ Create Clan</button>
            <button onClick={() => setJoining(s => !s)} style={{ flex: 1, padding: '10px', borderRadius: 10, background: 'none', border: '1.5px solid var(--bdr)', color: 'var(--txt)', fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>Join by Code</button>
          </div>
          {creating && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div className="st" style={{ marginBottom: 12 }}>Create Clan</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="ob-inp" placeholder="Emoji" value={form.emoji} onChange={(e) => setForm(f => ({ ...f, emoji: e.target.value }))} style={{ width: 60, margin: 0, textAlign: 'center' }} />
                <input className="ob-inp" placeholder="Clan name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={{ flex: 1, margin: 0 }} />
              </div>
              <input className="ob-inp" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} style={{ marginBottom: 8 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--mut)', marginBottom: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isPublic} onChange={(e) => setForm(f => ({ ...f, isPublic: e.target.checked }))} />
                Public clan (visible in browse)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleCreate} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer' }}>Create</button>
                <button onClick={() => setCreating(false)} style={{ padding: '9px 14px', borderRadius: 10, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
          {joining && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input className="ob-inp" placeholder="Enter invite code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} style={{ flex: 1, margin: 0, letterSpacing: 2, fontFamily: 'monospace' }} />
              <button onClick={handleJoin} style={{ padding: '9px 16px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer' }}>Join</button>
            </div>
          )}
        </>
      )}

      {view === 'browse' && (
        publicClans.length === 0 ? (
          <div className="em">No public clans yet — create one!</div>
        ) : (
          publicClans.map(c => (
            <div key={c.id} className="card cp" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => openClan(c.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 28 }}>{c.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>{c.name}</div>
                  {c.description && <div style={{ fontSize: 11, color: 'var(--mut)' }}>{c.description}</div>}
                  <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>{c.member_count} members · avg score {Math.round(c.avg_score || 0)}</div>
                </div>
                <span style={{ fontSize: 14, color: 'var(--mut)' }}>→</span>
              </div>
            </div>
          ))
        )
      )}
    </div>
  );
}

// ── Messages (DMs) ────────────────────────────────────────────────────────────

function MessagesView({ showToast }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const r = await getConversations();
    if (r.conversations) setConversations(r.conversations);
    setLoading(false);
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  const openConvo = async (convo) => {
    setActiveConvo(convo);
    const r = await getDMs(convo.other_id);
    if (r.messages) setMessages(r.messages);
    loadConvos(); // refresh unread counts
  };

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!msgText.trim() || !activeConvo) return;
    const r = await sendDM(activeConvo.other_id, msgText.trim());
    if (r.message) {
      setMessages(m => [...m, r.message]);
      setMsgText('');
    } else showToast(r.error || 'Failed to send');
  };

  if (activeConvo) {
    const name = activeConvo.other_display || activeConvo.other_username;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--bdr)' }}>
          <button onClick={() => { setActiveConvo(null); loadConvos(); }} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}>←</button>
          <Avatar emoji={activeConvo.other_emoji || '😊'} color={activeConvo.other_color || '#4d96ff'} size={32} />
          <span style={{ fontWeight: 700, fontSize: 14 }}>{name}</span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 8 }}>
          {messages.length === 0 ? (
            <div className="em">No messages yet — say hi!</div>
          ) : (
            messages.map(m => {
              const isMe = m.sender_id !== activeConvo.other_id;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', gap: 8 }}>
                  <div style={{
                    maxWidth: '70%', padding: '8px 12px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: isMe ? 'var(--ylw)' : 'var(--surf2)',
                    color: isMe ? '#000' : 'var(--txt)',
                    fontSize: 13,
                  }}>
                    {m.content}
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2, textAlign: isMe ? 'right' : 'left' }}>
                      {timeAgo(m.created_at)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid var(--bdr)' }}>
          <input
            className="ob-inp"
            placeholder="Message…"
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            style={{ flex: 1, margin: 0 }}
          />
          <button onClick={sendMessage} style={{ padding: '8px 16px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer' }}>
            Send
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="st" style={{ marginBottom: 12 }}>💬 Direct Messages</div>
      <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 14 }}>You can message friends. Add friends via Profile first.</div>
      {loading ? (
        <div className="em">Loading…</div>
      ) : conversations.length === 0 ? (
        <div className="em">No conversations yet — DM a friend!</div>
      ) : (
        conversations.map(c => (
          <div key={c.other_id} onClick={() => openConvo(c)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 12, border: '1px solid var(--bdr)', marginBottom: 8, cursor: 'pointer', background: 'var(--surf2)' }}>
            <Avatar emoji={c.other_emoji || '😊'} color={c.other_color || '#4d96ff'} size={38} />
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{c.other_display || c.other_username}</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.last_content}
              </div>
            </div>
            {c.unread_count > 0 && (
              <div style={{ background: 'var(--ylw)', color: '#000', borderRadius: '50%', fontSize: 10, fontWeight: 900, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {c.unread_count}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── Avatar Picker ─────────────────────────────────────────────────────────────

function AvatarPicker({ emoji, color, onChange }) {
  const [open, setOpen] = useState(false);
  const [localEmoji, setLocalEmoji] = useState(emoji);
  const [localColor, setLocalColor] = useState(color);

  const apply = () => {
    onChange({ emoji: localEmoji, color: localColor });
    setOpen(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Avatar emoji={localEmoji} color={localColor} size={52} />
        <button onClick={() => setOpen(s => !s)} style={{ padding: '6px 14px', borderRadius: 20, border: '1.5px solid var(--bdr)', background: 'none', color: 'var(--txt)', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
          {open ? 'Close' : '🎨 Edit Avatar'}
        </button>
      </div>
      {open && (
        <div style={{ padding: '12px', background: 'var(--surf2)', borderRadius: 12, border: '1px solid var(--bdr)', marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 6, fontWeight: 700 }}>Emoji</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {AVATAR_EMOJIS.map(em => (
              <button key={em} onClick={() => setLocalEmoji(em)} style={{ fontSize: 22, cursor: 'pointer', background: localEmoji === em ? 'rgba(255,255,255,.15)' : 'none', border: `2px solid ${localEmoji === em ? 'var(--ylw)' : 'transparent'}`, borderRadius: 8, padding: 4 }}>
                {em}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 6, fontWeight: 700 }}>Background Color</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {AVATAR_COLORS.map(col => (
              <button key={col} onClick={() => setLocalColor(col)} style={{ width: 28, height: 28, borderRadius: '50%', background: col, cursor: 'pointer', border: `3px solid ${localColor === col ? '#fff' : 'transparent'}`, boxSizing: 'border-box' }} />
            ))}
          </div>
          <button onClick={apply} style={{ padding: '7px 20px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 12 }}>
            Save Avatar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Recap Card ────────────────────────────────────────────────────────────────

function RecapCard({ profile, username, onClose }) {
  const now = new Date();
  const monthName = now.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0009', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 360, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 60px #000a' }}>
        <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 2, marginBottom: 8 }}>MY FINANCIAL RECAP</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 16 }}>{monthName}</div>
          <Avatar emoji={profile?.avatar_emoji || '😊'} color={profile?.avatar_color || '#4d96ff'} size={64} />
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginTop: 10 }}>{profile?.display_name || username}</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>@{username}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
              { l: 'Level', v: profile?.level || 1, c: '#ffd93d' },
              { l: 'Score', v: Math.round(profile?.rank_score || 0), c: '#6bcb77' },
              { l: 'Streak', v: (profile?.streak || 0) + 'mo 🔥', c: '#ff6b6b' },
              { l: 'Savings Rate', v: Math.round((profile?.savings_rate || 0) * 100) + '%', c: '#4d96ff' },
            ].map(s => (
              <div key={s.l} style={{ background: 'rgba(255,255,255,.06)', borderRadius: 12, padding: '12px 8px' }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: s.c }}>{s.v}</div>
                <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, color: '#555', marginTop: 8 }}>frujo.net · Finance Dashboard</div>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '14px', background: '#222', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ── Profile View ──────────────────────────────────────────────────────────────

export function ProfileView({ username, showToast, storeEquipped }) {
  const [profile, setProfile] = useState(null);
  const [friends, setFriends] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [accountability, setAccountability] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [friendInput, setFriendInput] = useState('');
  const [buddyInput, setBuddyInput] = useState('');
  const [editing, setEditing] = useState(false);
  const [profileTab, setProfileTab] = useState('profile');
  const [showRecap, setShowRecap] = useState(false);

  const load = useCallback(async () => {
    const [p, f, n, a] = await Promise.all([getSocialProfile(), getFriends(), getNotifications(), getAccountability()]);
    setProfile(p);
    setDisplayName(p.display_name || username);
    if (f.friends) setFriends(f.friends);
    if (n.notifications) setNotifications(n.notifications);
    setAccountability(a.pair || null);
  }, [username]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    await updateSocialProfile({ displayName, optInSocial: profile?.opt_in_social });
    showToast('Profile updated!');
    setEditing(false);
    load();
  };

  const saveAvatar = async ({ emoji, color }) => {
    await updateSocialProfile({ displayName: profile?.display_name || username, optInSocial: profile?.opt_in_social, avatarEmoji: emoji, avatarColor: color });
    showToast('Avatar updated! 🎨');
    load();
  };

  const toggleOptIn = async () => {
    const next = !profile?.opt_in_social;
    await updateSocialProfile({ displayName: profile?.display_name || username, optInSocial: next });
    showToast(next ? "✅ You're on the leaderboard!" : 'Removed from leaderboard');
    load();
  };

  const sendRequest = async () => {
    if (!friendInput.trim()) return;
    const r = await sendFriendRequest(friendInput.trim());
    if (r.ok) { showToast('Friend request sent!'); setFriendInput(''); load(); }
    else showToast(r.error || 'Failed');
  };

  const readAll = async () => {
    await markNotificationsRead();
    setNotifications(n => n.map(x => ({ ...x, read: true })));
    showToast('Notifications cleared');
  };

  const accepted = friends.filter(f => f.status === 'accepted');
  const received = friends.filter(f => f.status === 'pending' && f.direction === 'received');
  const unreadCount = notifications.filter(n => !n.read).length;

  const PROFILE_TABS = [
    { id: 'profile', label: '👤 Profile' },
    { id: 'friends', label: `👥 Friends${received.length ? ` (${received.length})` : ''}` },
    { id: 'buddy',   label: `🤝 Buddy${accountability?.status === 'pending' && accountability?.direction === 'received' ? ' (1)' : ''}` },
    { id: 'badges',  label: '🏅 Badges' },
    { id: 'notifs',  label: `🔔 Alerts${unreadCount ? ` (${unreadCount})` : ''}` },
  ];

  return (
    <div>
      <TabBar tabs={PROFILE_TABS} active={profileTab} onChange={setProfileTab} />

      {/* ── Profile tab ── */}
      {profileTab === 'profile' && (
        <>
          <div className="card cp" style={{ marginBottom: 12 }}>
            <AvatarPicker emoji={profile?.avatar_emoji || '😊'} color={profile?.avatar_color || '#4d96ff'} onChange={saveAvatar} />

            <div style={{ flex: 1, marginBottom: 12 }}>
              {editing ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input className="ob-inp" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={{ margin: 0, fontSize: 14 }} maxLength={30} />
                  <button onClick={saveProfile} style={{ padding: '4px 10px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 11 }}>Save</button>
                  <button onClick={() => setEditing(false)} style={{ padding: '4px 8px', borderRadius: 8, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 11, cursor: 'pointer' }}>✕</button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{profile?.display_name || username}</span>
                    <LvBadge level={profile?.level} size={14} />
                    <button onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}>✏️</button>
                  </div>
                  {(() => {
                    const titleId = storeEquipped?.profileTitle;
                    if (!titleId) return null;
                    const item = STORE_CATALOG.find(i => i.id === titleId);
                    if (!item) return null;
                    return (
                      <div style={{ fontSize: 11, color: 'var(--ylw)', fontWeight: 700, marginTop: 3 }}>
                        {item.em} {item.value}
                      </div>
                    );
                  })()}
                </>
              )}
              <div style={{ fontSize: 11, color: 'var(--mut)' }}>@{username}</div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              {[
                { l: 'Level', v: profile?.level || 1, c: 'var(--ylw)' },
                { l: 'XP', v: (profile?.xp || 0).toLocaleString(), c: 'var(--ylw)' },
                { l: 'Streak', v: (profile?.streak || 0) + 'mo', c: 'var(--grn)' },
                { l: 'Score', v: Math.round(profile?.rank_score || 0), c: 'var(--blu)' },
              ].map(s => (
                <div key={s.l} style={{ flex: 1, minWidth: 70, padding: '8px 10px', background: 'var(--surf2)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: s.c }}>{s.v}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>{s.l}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid var(--bdr)', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>🏆 Appear on leaderboard</div>
                <div style={{ fontSize: 11, color: 'var(--mut)' }}>Only level, score, streak — never dollar amounts</div>
              </div>
              <label className="tog" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={!!profile?.opt_in_social} onChange={toggleOptIn} />
                <span className="tog-sw" />
              </label>
            </div>

            <button onClick={() => setShowRecap(true)} style={{ width: '100%', padding: '9px', borderRadius: 10, background: 'none', border: '1.5px solid var(--bdr)', color: 'var(--txt)', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
              📊 View Monthly Recap Card
            </button>
          </div>

          {showRecap && <RecapCard profile={profile} username={username} onClose={() => setShowRecap(false)} />}
        </>
      )}

      {/* ── Friends tab ── */}
      {profileTab === 'friends' && (
        <div className="card cp">
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input className="ob-inp" placeholder="Add friend by username…" value={friendInput} onChange={(e) => setFriendInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendRequest()} style={{ flex: 1, margin: 0 }} />
            <button onClick={sendRequest} style={{ padding: '8px 14px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 12 }}>Add</button>
          </div>

          {received.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ylw)', marginBottom: 6 }}>📬 Friend Requests</div>
              {received.map(f => (
                <div key={f.requester_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
                  <Avatar emoji={f.avatar_emoji || '😊'} color={f.avatar_color || '#4d96ff'} size={32} />
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{f.display_name || f.username}</div>
                  <button onClick={async () => { await acceptFriendRequest(f.requester_id); load(); }} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, border: '1px solid var(--grn)', color: 'var(--grn)', background: 'none', cursor: 'pointer', fontWeight: 700 }}>Accept</button>
                  <button onClick={async () => { await removeFriend(f.requester_id); load(); }} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 8, border: '1px solid var(--bdr)', color: 'var(--mut)', background: 'none', cursor: 'pointer' }}>✕</button>
                </div>
              ))}
            </>
          )}

          {accepted.length === 0 ? (
            <div className="em">No friends yet — add someone!</div>
          ) : (
            accepted.map(f => (
              <div key={f.friend_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
                <Avatar emoji={f.avatar_emoji || '😊'} color={f.avatar_color || '#4d96ff'} size={32} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.display_name || f.username}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>🔥 {f.streak || 0}mo streak</div>
                </div>
                <LvBadge level={f.level} />
                <button onClick={async () => { await removeFriend(f.friend_id); load(); }} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}>✕</button>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Accountability Buddy tab ── */}
      {profileTab === 'buddy' && (
        <div className="card cp">
          <div className="st" style={{ marginBottom: 6 }}>🤝 Accountability Buddy</div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 14 }}>
            Pair with one friend. You'll each be quietly notified if the other goes over budget — motivation through friendly pressure.
          </div>
          {!accountability ? (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="ob-inp" placeholder="Friend's username…" value={buddyInput} onChange={e => setBuddyInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && requestAccountabilityBuddy(buddyInput.trim()).then(r => { if (r.ok) { showToast('Request sent!'); setBuddyInput(''); load(); } else showToast(r.error || 'Failed'); })} style={{ flex: 1, margin: 0 }} />
                <button onClick={async () => { const r = await requestAccountabilityBuddy(buddyInput.trim()); if (r.ok) { showToast('Request sent!'); setBuddyInput(''); load(); } else showToast(r.error || 'Failed'); }} style={{ padding: '8px 14px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 12 }}>
                  Request
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--mut)' }}>Must be friends first. Only one buddy at a time.</div>
            </>
          ) : accountability.status === 'pending' ? (
            <div style={{ padding: 14, background: 'var(--surf2)', borderRadius: 12, border: '1px solid var(--bdr)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Avatar emoji={accountability.buddy_emoji || '😊'} color={accountability.buddy_color || '#4d96ff'} size={36} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{accountability.buddy_display || accountability.buddy_username}</div>
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>{accountability.direction === 'received' ? 'Wants to be your accountability buddy' : 'Request sent — waiting…'}</div>
                </div>
              </div>
              {accountability.direction === 'received' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={async () => { await acceptAccountabilityBuddy(accountability.user_id); showToast('Buddy accepted! 🤝'); load(); }} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 13 }}>Accept 🤝</button>
                  <button onClick={async () => { await removeAccountabilityBuddy(); load(); }} style={{ padding: '9px 14px', borderRadius: 10, background: 'none', border: '1px solid var(--red)', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>Decline</button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 14, background: 'rgba(107,203,119,.08)', borderRadius: 12, border: '1px solid rgba(107,203,119,.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Avatar emoji={accountability.buddy_emoji || '😊'} color={accountability.buddy_color || '#4d96ff'} size={38} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>{accountability.buddy_display || accountability.buddy_username}</div>
                  <div style={{ fontSize: 11, color: 'var(--grn)', fontWeight: 700 }}>✓ Active accountability buddy</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>You'll be notified if they go over budget, and vice versa.</div>
                </div>
                <LvBadge level={accountability.buddy_level} />
              </div>
              <button onClick={async () => { if (!window.confirm('Remove accountability buddy?')) return; await removeAccountabilityBuddy(); showToast('Buddy removed'); load(); }} style={{ width: '100%', padding: '8px', borderRadius: 10, background: 'none', border: '1px solid var(--red)', color: 'var(--red)', fontSize: 12, cursor: 'pointer' }}>
                Remove Buddy
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Badges tab ── */}
      {profileTab === 'badges' && (
        <div className="card cp">
          <div className="st" style={{ marginBottom: 12 }}>🏅 Your Badges</div>
          {BADGES.map(b => {
            const earned = (profile?.level || 1) >= b.level;
            return (
              <div key={b.level} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--bdr)', opacity: earned ? 1 : 0.35 }}>
                <div style={{ fontSize: 32, filter: earned ? 'none' : 'grayscale(1)' }}>{b.em}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: earned ? 'var(--txt)' : 'var(--mut)' }}>{b.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>Unlocked at Level {b.level}{!earned ? ` · ${b.level - (profile?.level || 1)} levels away` : ' ✓'}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Notifications tab ── */}
      {profileTab === 'notifs' && (
        <div>
          {unreadCount > 0 && (
            <button onClick={readAll} style={{ marginBottom: 12, padding: '6px 14px', borderRadius: 20, border: '1.5px solid var(--bdr)', background: 'none', color: 'var(--mut)', fontSize: 12, cursor: 'pointer' }}>
              Mark all read
            </button>
          )}
          {notifications.length === 0 ? (
            <div className="em">No notifications yet.</div>
          ) : (
            notifications.map(n => {
              const label = n.type === 'dm' ? `New DM from someone` : n.type === 'friend_request' ? 'New friend request' : n.type === 'battle' ? 'Battle invitation' : n.type;
              return (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bdr)', opacity: n.read ? 0.5 : 1 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.read ? 'transparent' : 'var(--ylw)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: n.read ? 400 : 700 }}>{label}</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Boss Battle View ─────────────────────────────────────────────────────────

function BossView({ showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hitting, setHitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getBoss().then(r => { setData(r); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="em" style={{ marginTop: 14 }}>Loading boss…</div>;
  if (!data?.boss) return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>No active boss right now</div>
      <div style={{ fontSize: 12, color: 'var(--mut)' }}>A new boss spawns each month. Check back soon!</div>
    </div>
  );

  const { boss, myHit, topDamage } = data;
  const hpPct = Math.round((boss.hp_remaining / boss.hp_total) * 100);
  const hpCol = hpPct > 60 ? 'var(--grn)' : hpPct > 30 ? 'var(--ylw)' : 'var(--red)';
  const daysLeft = Math.max(0, Math.ceil((new Date(boss.ends_at) - new Date()) / 86400000));

  const strike = async (dmg) => {
    setHitting(true);
    const r = await hitBoss(dmg);
    if (r.ok) {
      showToast(`💥 ${dmg} damage dealt!`);
      if (r.hpRemaining === 0) showToast(`🎉 Boss slain! +${boss.xp_reward} XP incoming!`);
      load();
    }
    setHitting(false);
  };

  return (
    <div>
      <div className="card cp" style={{ marginBottom: 14, textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 8 }}>{boss.emoji}</div>
        <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>{boss.title}</div>
        <div style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 16 }}>{boss.description}</div>

        {/* HP bar */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginBottom: 4 }}>
            <span style={{ color: hpCol, fontWeight: 700 }}>{hpPct}% HP</span>
            <span>{boss.hp_remaining.toLocaleString()} / {boss.hp_total.toLocaleString()}</span>
          </div>
          <div style={{ height: 14, background: 'var(--surf2)', borderRadius: 999, overflow: 'hidden', border: `1px solid ${hpCol}44` }}>
            <div style={{ height: '100%', width: hpPct + '%', background: hpCol, borderRadius: 999, transition: 'width .6s' }} />
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 16 }}>
          ⏳ {daysLeft} days left · 🏆 +{boss.xp_reward} XP reward for all who participated
        </div>

        {/* My contribution */}
        {myHit && (
          <div style={{ padding: '8px 16px', background: 'rgba(107,203,119,.1)', border: '1px solid rgba(107,203,119,.3)', borderRadius: 10, marginBottom: 14, fontSize: 12 }}>
            Your damage: <strong style={{ color: 'var(--grn)' }}>{myHit.damage.toLocaleString()}</strong> across <strong>{myHit.hits}</strong> hits
          </div>
        )}

        {/* Strike buttons */}
        {boss.hp_remaining > 0 && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 8 }}>Deal damage by logging good financial behavior:</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { label: 'Logged spending', dmg: 15, em: '📝' },
                { label: 'No-spend day',    dmg: 25, em: '🛑' },
                { label: 'Under budget',    dmg: 50, em: '📊' },
                { label: 'Paid debt extra', dmg: 75, em: '💳' },
              ].map(a => (
                <button
                  key={a.label}
                  onClick={() => strike(a.dmg)}
                  disabled={hitting}
                  style={{ padding: '8px 14px', borderRadius: 12, background: 'var(--surf2)', border: '1.5px solid var(--bdr)', color: 'var(--txt)', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}
                >
                  {a.em} {a.label}<br />
                  <span style={{ color: 'var(--red)', fontSize: 10 }}>-{a.dmg} HP</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {boss.hp_remaining === 0 && (
          <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--grn)' }}>⚔️ SLAIN! XP rewards sent.</div>
        )}
      </div>

      {/* Top damage dealers */}
      {topDamage.length > 0 && (
        <div className="card cp">
          <div className="st" style={{ marginBottom: 10 }}>💥 Top Damage Dealers</div>
          {topDamage.map((h, i) => (
            <div key={h.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
              <span style={{ width: 24, fontSize: 14, textAlign: 'center' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</span>
              <Avatar emoji={h.avatar_emoji || '😊'} color={h.avatar_color || '#4d96ff'} size={30} />
              <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{h.display_name || h.username}</div>
              <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--red)' }}>{h.damage.toLocaleString()} dmg</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Public Goals View ─────────────────────────────────────────────────────────

function GoalsView({ username, showToast }) {
  const [goals, setGoals] = useState([]);
  const [mine, setMine] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', emoji: '🎯', targetPct: 100, currentPct: 0 });
  const [view, setView] = useState('all');

  const load = useCallback(async () => {
    const [all, my] = await Promise.all([getPublicGoals(), getMyPublicGoals()]);
    if (all.goals) setGoals(all.goals);
    if (my.goals) setMine(my.goals);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addGoal = async () => {
    if (!form.name.trim()) return;
    const r = await createPublicGoal(form);
    if (r.goal) { showToast('Goal shared! 🎯'); setAdding(false); setForm({ name: '', emoji: '🎯', targetPct: 100, currentPct: 0 }); load(); }
    else showToast(r.error || 'Failed');
  };

  const updateProgress = async (id, pct) => {
    await updatePublicGoal(id, pct);
    load();
  };

  const GoalCard = ({ g, isOwn }) => {
    const [editing, setEditing] = useState(false);
    const [pct, setPct] = useState(g.current_pct);
    return (
      <div style={{ background: 'var(--surf2)', borderRadius: 12, border: '1px solid var(--bdr)', padding: '14px', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Avatar emoji={g.avatar_emoji || '😊'} color={g.avatar_color || '#4d96ff'} size={30} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{g.display_name || g.username}</div>
            <div style={{ fontSize: 10, color: 'var(--mut)' }}>{timeAgo(g.updated_at)}</div>
          </div>
          <span style={{ fontSize: 22 }}>{g.emoji}</span>
          {isOwn && (
            <button onClick={async () => { await deletePublicGoal(g.id); load(); }} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}>✕</button>
          )}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>{g.name}</div>
        <div style={{ height: 10, background: 'var(--surf)', borderRadius: 999, overflow: 'hidden', marginBottom: 6 }}>
          <div style={{ height: '100%', width: g.current_pct + '%', background: g.current_pct >= 100 ? 'var(--grn)' : 'var(--ylw)', borderRadius: 999, transition: 'width .5s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--mut)', marginBottom: isOwn ? 10 : 0 }}>
          <span style={{ fontWeight: 700, color: 'var(--ylw)' }}>{g.current_pct}% complete</span>
          <span>{g.current_pct >= 100 ? '🎉 Done!' : `${g.target_pct - g.current_pct}% to go`}</span>
        </div>
        {isOwn && (
          editing ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="range" min={0} max={100} value={pct} onChange={e => setPct(+e.target.value)} style={{ flex: 1 }} />
              <span style={{ fontSize: 12, fontWeight: 700, minWidth: 36 }}>{pct}%</span>
              <button onClick={() => { updateProgress(g.id, pct); setEditing(false); }} style={{ padding: '4px 12px', borderRadius: 8, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer', fontSize: 11 }}>Save</button>
            </div>
          ) : (
            <button onClick={() => setEditing(true)} style={{ width: '100%', padding: '7px', borderRadius: 8, background: 'rgba(255,217,61,.1)', border: '1.5px solid rgba(255,217,61,.3)', color: 'var(--ylw)', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
              Update Progress
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <div>
      <TabBar tabs={[{ id: 'all', label: '🌍 Everyone' }, { id: 'mine', label: '👤 My Goals' }]} active={view} onChange={setView} />

      {view === 'mine' && (
        <>
          <button onClick={() => setAdding(s => !s)} style={{ width: '100%', padding: '10px', borderRadius: 12, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer', marginBottom: 12 }}>
            {adding ? 'Cancel' : '+ Share a Goal'}
          </button>
          {adding && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input className="ob-inp" placeholder="Emoji" value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} style={{ width: 60, margin: 0, textAlign: 'center' }} />
                <input className="ob-inp" placeholder="Goal name (e.g. Pay off car loan)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ flex: 1, margin: 0 }} />
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--mut)', whiteSpace: 'nowrap' }}>Starting at:</span>
                <input type="range" min={0} max={100} value={form.currentPct} onChange={e => setForm(f => ({ ...f, currentPct: +e.target.value }))} style={{ flex: 1 }} />
                <span style={{ fontSize: 12, fontWeight: 700, minWidth: 36 }}>{form.currentPct}%</span>
              </div>
              <button onClick={addGoal} style={{ width: '100%', padding: '9px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, border: 'none', cursor: 'pointer' }}>Share Goal</button>
            </div>
          )}
          {mine.length === 0 ? <div className="em">No public goals yet — share one!</div> : mine.map(g => <GoalCard key={g.id} g={g} isOwn />)}
        </>
      )}

      {view === 'all' && (
        goals.length === 0 ? <div className="em">No public goals yet — be the first!</div> : goals.map(g => <GoalCard key={g.id} g={g} isOwn={g.username === username} />)
      )}
    </div>
  );
}

// ── Main SocialPage ───────────────────────────────────────────────────────────

export default function SocialPage({ state, showToast }) {
  const [inner, setInner] = useState('feed');
  const [compete, setCompete] = useState('ranks');
  const username = state._username || 'you';
  const levelInfo = getLevelInfo ? getLevelInfo(state) : null;
  const profile = null; // loaded fresh inside each view

  return (
    <div>
      <TabBar tabs={INNER_TABS} active={inner} onChange={setInner} />
      {inner === 'feed'     && <FeedView profile={profile} levelInfo={levelInfo} showToast={showToast} />}
      {inner === 'compete'  && (
        <>
          <TabBar tabs={COMPETE_TABS} active={compete} onChange={setCompete} />
          {compete === 'ranks' && <RanksView username={username} showToast={showToast} />}
          {compete === 'clans' && <ClansView username={username} showToast={showToast} levelInfo={levelInfo} />}
          {compete === 'boss'  && <BossView showToast={showToast} />}
          {compete === 'goals' && <GoalsView username={username} showToast={showToast} />}
        </>
      )}
      {inner === 'messages' && <MessagesView showToast={showToast} />}
    </div>
  );
}
